import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Tab, Tabs, Chip, Table,
  TableBody, TableCell, TableHead, TableRow, Paper
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { usePlannerTheme, usePlannerToolbarTheme } from '../../theme/plannerTheme';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import AssessmentIcon from '@material-ui/icons/Assessment';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HotelIcon from '@material-ui/icons/Hotel';
import moment from 'moment';
import 'moment/locale/pt';
import { setShowReports, selectUI } from '../../store/slices/uiSlice';
import { selectCurrentRoute } from '../../store/slices/routesSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { selectDrivers } from '../../store/slices/driversSlice';
import { t } from '../../i18n';
import GlassCard from '../common/GlassCard';
import SectionTitle from '../common/SectionTitle';
import ExportBar from './ExportBar';
import { FUEL_TYPES } from '../Sidebar/VehicleDriverForm';
import { getCurrencySymbol } from '../../utils/currencies';

moment.locale('pt');

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.5)',
    backdropFilter: 'blur(8px)',
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  modal: {
    width: '100%',
    maxWidth: 900,
    maxHeight: '90vh',
    background: 'var(--surf-card, #ffffff)',
    border: '1px solid var(--surf-border, rgba(0,0,0,0.08))',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
  },
  modalHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid rgba(0,0,0,0.07)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    background: 'var(--surf-head-bg, linear-gradient(180deg, #ffffff 0%, #dbeafe 100%))',
  },
  tabs: {
    borderBottom: '1px solid rgba(0,0,0,0.07)',
    flexShrink: 0,
    '& .MuiTabs-indicator': {
      background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
    },
  },
  tabContent: {
    flex: 1,
    overflow: 'auto',
    padding: 24,
    background: 'var(--surf-page, #f8fafc)',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
    marginBottom: 20,
  },
  summaryCard: {
    padding: '14px 16px',
    textAlign: 'center',
  },
  exportSection: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(0,0,0,0.07)',
    flexShrink: 0,
    background: 'var(--surf-page, #f8fafc)',
  },
  tableCell: {
    fontSize: '0.78rem',
    color: 'var(--surf-text, #475569)',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    padding: '8px 12px',
  },
  tableHeadCell: {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: 'var(--surf-muted2, #94a3b8)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    padding: '8px 12px',
    background: '#f1f5f9',
  },
}));

function SummaryCard({ label, value, unit, color }) {
  const classes = useStyles();
  return (
    <GlassCard className={classes.summaryCard} flat>
      <Typography style={{ fontSize: '0.6rem', color: 'var(--surf-muted2, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
        {label}
      </Typography>
      <Typography style={{ fontSize: '1.4rem', fontWeight: 700, color: color || '#0f172a', lineHeight: 1 }}>
        {value ?? '—'}
      </Typography>
      {unit && <Typography style={{ fontSize: '0.65rem', color: 'var(--surf-muted, #64748b)', marginTop: 2 }}>{unit}</Typography>}
    </GlassCard>
  );
}

function RouteReport({ route, vehicle, driver }) {
  const classes = useStyles();
  const cr = route.computedRoute;
  const durationMin = cr ? Math.round(cr.duration / 60) : 0;
  const distanceKm = cr ? (cr.distance / 1000).toFixed(1) : '—';
  const fuelType = route.fuelType || 'diesel';
  const fuelDef = FUEL_TYPES.find(f => f.value === fuelType) || FUEL_TYPES[0];
  const consumption = route.fuelConsumption ?? vehicle?.fuelPer100km ?? fuelDef.consumptionDefault;
  const distKm = cr ? cr.distance / 1000 : 0;
  const totalFuel = cr ? (distKm / 100) * consumption : 0;
  const fuelEst = cr ? totalFuel.toFixed(1) : '—';
  const pricePerUnit = route.fuelPrice ?? fuelDef.priceDefault;
  const currencyCode = route.routeOptions?.currency || 'EUR';
  const currencySymbol = getCurrencySymbol(currencyCode);
  const fuelCost = cr ? (totalFuel * pricePerUnit).toLocaleString('pt-PT', { maximumFractionDigits: 0 }) : '—';
  const exceedsDaily = durationMin > 540;

  return (
    <Box>
      {/* Summary metrics */}
      <Box className={classes.summaryGrid}>
        <SummaryCard label="Distância" value={distanceKm} unit="km" color="#3b82f6" />
        <SummaryCard
          label="Duração"
          value={`${Math.floor(durationMin / 60)}h ${durationMin % 60}min`}
          color="#6366f1"
        />
        <SummaryCard label="Paragens" value={route.stops.length} color="#f59e0b" />
        <SummaryCard label="Combustível Est." value={fuelEst} unit={fuelDef.unit} color="#22c55e" />
        <SummaryCard label="Custo Combustível" value={fuelCost} unit={currencySymbol} color="#f59e0b" />
        <SummaryCard
          label="Limite Diário"
          value={exceedsDaily ? 'EXCEDE' : 'OK'}
          color={exceedsDaily ? '#ef4444' : '#22c55e'}
        />
        <SummaryCard
          label="Conformidade EU"
          value={exceedsDaily ? 'Não conforme' : 'Conforme'}
          color={exceedsDaily ? '#ef4444' : '#22c55e'}
        />
      </Box>

      {/* Route details */}
      <Box style={{ marginBottom: 20 }}>
        <SectionTitle>Detalhes da Rota</SectionTitle>
        <GlassCard flat padding="sm" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
          {[
            ['Nome', route.name || '(sem nome)'],
            ['Origem', route.origin?.name || '—'],
            ['Destino', route.destination?.name || '—'],
            ['Carga/Guia', route.cargoRef || '—'],
            ['Partida', route.departureTime ? moment(route.departureTime).format('DD/MM/YYYY HH:mm') : '—'],
            ['Veículo', vehicle ? `${vehicle.plate} (${vehicle.type})` : '—'],
            ['Motorista', driver ? driver.name : '—'],
            ['Cartão Tacho', driver ? driver.tachoCard : '—'],
          ].map(([label, value]) => (
            <Box key={label} style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography style={{ fontSize: '0.6rem', color: 'var(--surf-muted2, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
              <Typography style={{ fontSize: '0.8rem', color: 'var(--surf-title, #0f172a)' }}>{value}</Typography>
            </Box>
          ))}
        </GlassCard>
      </Box>

      {/* Stops table */}
      {route.stops.length > 0 && (
        <Box>
          <SectionTitle icon={HotelIcon}>Paragens de Descanso</SectionTitle>
          <Paper style={{ background: 'transparent', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 8, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['#', 'Tipo', 'Km', 'Condução Antes', 'Duração', 'Chegada Est.'].map(h => (
                    <TableCell key={h} className={classes.tableHeadCell}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {route.stops.map((stop, idx) => (
                  <TableRow key={idx} style={{ '&:hover': { background: 'rgba(255,255,255,0.02)' } }}>
                    <TableCell className={classes.tableCell}>{idx + 1}</TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        label="Pausa 45min"
                        size="small"
                        style={{ height: 18, fontSize: '0.6rem', background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>{stop.km} km</TableCell>
                    <TableCell className={classes.tableCell}>
                      {Math.floor(stop.drivingMinBefore / 60)}h {stop.drivingMinBefore % 60}min
                    </TableCell>
                    <TableCell className={classes.tableCell}>{stop.duration}min</TableCell>
                    <TableCell className={classes.tableCell}>{stop.arrivalTime || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {route.stops.length === 0 && (
        <GlassCard flat padding="sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircleIcon style={{ color: '#22c55e', fontSize: 20 }} />
          <Typography style={{ fontSize: '0.8rem', color: '#22c55e' }}>
            Nenhuma paragem obrigatória — rota dentro do limite de 4h30 de condução
          </Typography>
        </GlassCard>
      )}
    </Box>
  );
}

function NonConformityReport({ route }) {
  const durationMin = route.computedRoute ? Math.round(route.computedRoute.duration / 60) : 0;
  const exceedsDaily = durationMin > 540;
  const exceedsContinuous = durationMin > 270 && route.stops.length === 0;

  const issues = [
    exceedsDaily && {
      severity: 'high',
      rule: 'Art. 6 Reg. 561/2006',
      description: `Tempo de condução diário excede 9h (atual: ${Math.floor(durationMin / 60)}h ${durationMin % 60}min)`,
    },
    exceedsContinuous && {
      severity: 'high',
      rule: 'Art. 7 Reg. 561/2006',
      description: 'Condução contínua superior a 4h30 sem pausa registada',
    },
  ].filter(Boolean);

  return (
    <Box>
      <Box style={{ marginBottom: 16 }}>
        <GlassCard flat padding="sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {issues.length === 0 ? (
            <>
              <CheckCircleIcon style={{ color: '#22c55e', fontSize: 20 }} />
              <Box>
                <Typography style={{ fontSize: '0.82rem', color: '#22c55e', fontWeight: 600 }}>
                  Sem inconformidades detectadas
                </Typography>
                <Typography style={{ fontSize: '0.7rem', color: 'var(--surf-muted, #64748b)' }}>
                  Rota conforme com o Regulamento (CE) n.º 561/2006
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <WarningIcon style={{ color: '#ef4444', fontSize: 20 }} />
              <Box>
                <Typography style={{ fontSize: '0.82rem', color: '#ef4444', fontWeight: 600 }}>
                  {issues.length} inconformidade(s) detectada(s)
                </Typography>
              </Box>
            </>
          )}
        </GlassCard>
      </Box>

      {issues.map((issue, idx) => (
        <GlassCard
          key={idx}
          flat
          padding="sm"
          style={{
            borderLeft: '3px solid #ef4444',
            marginBottom: 10,
            background: 'rgba(239,68,68,0.04)',
          }}
        >
          <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#ef4444' }}>
              {issue.rule}
            </Typography>
            <Chip
              label="Grave"
              size="small"
              style={{ height: 16, fontSize: '0.55rem', background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}
            />
          </Box>
          <Typography style={{ fontSize: '0.78rem', color: 'var(--surf-title, #0f172a)' }}>{issue.description}</Typography>
        </GlassCard>
      ))}
    </Box>
  );
}

function ReportsModal() {
  const classes = useStyles();
  // Novo UI só em Claro/Escuro (Clássico usa os fallbacks dos var(--surf-*)).
  const theme = usePlannerTheme();
  const surfTheme = usePlannerToolbarTheme() !== 'classic' ? theme : undefined;
  const dispatch = useDispatch();
  const { lang } = useSelector(selectUI);
  const route = useSelector(selectCurrentRoute);
  const vehicles = useSelector(selectVehicles);
  const drivers = useSelector(selectDrivers);
  const [tab, setTab] = useState(0);

  const vehicle = vehicles.find(v => v.id === route.vehicleId);
  const driver = drivers.find(d => d.id === route.driverId);

  return (
    <Box className={classes.overlay} data-planner-theme={surfTheme} onClick={e => e.target === e.currentTarget && dispatch(setShowReports(false))}>
      <Box className={classes.modal}>
        {/* Header */}
        <Box className={classes.modalHeader}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AssessmentIcon style={{ color: '#3b82f6', fontSize: 20 }} />
            <Box>
              <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--surf-title, #0f172a)' }}>
                {t(lang, 'reports')}
              </Typography>
              {route.name && (
                <Typography style={{ fontSize: '0.7rem', color: 'var(--surf-muted, #64748b)' }}>
                  {route.name}{route.cargoRef ? ` · ${route.cargoRef}` : ''}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={() => dispatch(setShowReports(false))} style={{ color: 'var(--surf-muted2, #94a3b8)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          className={classes.tabs}
        >
          <Tab label={t(lang, 'routeReport')} style={{ textTransform: 'none', fontSize: '0.8rem' }} />
          <Tab label={t(lang, 'nonConformity')} style={{ textTransform: 'none', fontSize: '0.8rem' }} />
        </Tabs>

        {/* Content */}
        <Box className={classes.tabContent}>
          {tab === 0 && <RouteReport route={route} vehicle={vehicle} driver={driver} lang={lang} />}
          {tab === 1 && <NonConformityReport route={route} lang={lang} />}
        </Box>

        {/* Export bar */}
        <Box className={classes.exportSection}>
          <Typography style={{ fontSize: '0.65rem', color: 'var(--surf-muted2, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Exportar Relatório
          </Typography>
          <ExportBar />
        </Box>
      </Box>
    </Box>
  );
}

export default ReportsModal;
